interface ImageHandlerInput {
  builderMode?: boolean;
  imageUrl: string;
  MediaMap?: Map<string, any>;
}

interface ImageHandlerOutput {
  currentSrc: string;
  srcset: string;
  sizes: string;
  fallbackUrl: string;
}

/**
 * Parse a srcset string into an array of sources with width info.
 */
const parseSourceSet = (srcsetString: string): { url: string; width: number }[] => {
  if (!srcsetString) return [];
  return srcsetString.split(',').map((source) => {
    const [url, width] = source.trim().split(' ');
    return {
      url: url.trim(),
      width: parseInt(width.replace('w', '')),
    };
  });
};

/**
 * Pure function to generate image props for SSR use.
 */
export const getImageHandlerSSR = ({ builderMode, imageUrl, MediaMap }: ImageHandlerInput): ImageHandlerOutput => {
  if (builderMode || !MediaMap) {
    return {
      currentSrc: imageUrl,
      srcset: '',
      sizes: '',
      fallbackUrl: '',
    };
  }

  try {
    const imageData = MediaMap.get(imageUrl);

    // Handle simple URL object (Next.js / Multiverse)
    if (imageData && imageData.url && !imageData.localFile) {
      return {
        currentSrc: imageData.url,
        srcset: '',
        sizes: '',
        fallbackUrl: imageData.blurDataUrl || '',
      };
    }

    if (!imageData || !imageData.localFile) {
      // Instead of throwing, just return the original URL to prevent crashes
      return {
        currentSrc: imageUrl,
        srcset: '',
        sizes: '',
        fallbackUrl: '',
      };
    }

    const gatsbyImageData = imageData.localFile.childImageSharp.gatsbyImageData;
    // ... rest of the Gatsby logic could remain if needed, but for now we just need to stop the crash.
    // However, since we are in a shared file, we should keep the Gatsby logic for the Gatsby app.

    const source = gatsbyImageData.images.sources?.[0];
    const fallback = gatsbyImageData.images.fallback;

    if (source?.srcSet) {
      const sources = parseSourceSet(source.srcSet);
      const srcset = sources.map((s) => `${s.url} ${s.width}w`).join(', ');
      const defaultSrc = sources.reduce((largest, current) => (current.width > largest.width ? current : largest)).url;

      return {
        currentSrc: defaultSrc || fallback?.src || imageUrl,
        srcset,
        sizes: source.sizes || '',
        fallbackUrl: gatsbyImageData.placeholder?.fallback || '',
      };
    } else if (fallback?.srcSet) {
      const sources = parseSourceSet(fallback.srcSet);
      const srcset = sources.map((s) => `${s.url} ${s.width}w`).join(', ');
      return {
        currentSrc: fallback.src || imageUrl,
        srcset,
        sizes: fallback.sizes || '',
        fallbackUrl: gatsbyImageData.placeholder?.fallback || '',
      };
    }

    return {
      currentSrc: fallback?.src || imageUrl,
      srcset: '',
      sizes: '',
      fallbackUrl: gatsbyImageData.placeholder?.fallback || '',
    };
  } catch (error) {
    console.error('SSR Image processing error:', error);
    return {
      currentSrc: imageUrl,
      srcset: '',
      sizes: '',
      fallbackUrl: '',
    };
  }
};
